import { DeviceInfo } from "../../services/vision/types";

// Standard field labels mapping
export const createFieldLabels = (): Record<string, string> => ({
  type: "Device Type",
  make: "Manufacturer",
  model: "Model",
  name: "Device Name",
  serialNumber: "Serial Number",
  imei: "IMEI",
  phoneNumber: "Phone Number",
  osName: "Operating System",
  osType: "OS Type",
  osVersion: "OS Version",
  processor: "Processor",
  chipset: "Chipset",
  ram: "Memory",
  storage: "Storage",
  availableStorage: "Available Storage",
  totalStorage: "Total Storage",
  startupDisk: "Startup Disk",
  displaySize: "Display Size",
  displayResolution: "Display Resolution",
  displayType: "Display Type",
  year: "Year",
  color: "Color",
  batteryLevel: "Battery Level",
  batteryHealth: "Battery Health",
  batteryCapacity: "Battery Capacity",
  macOSVersion: "macOS Version",
  iosVersion: "iOS Version",
  androidVersion: "Android Version",
  modelName: "Model Name",
  modelNumber: "Model Number",
  modelIdentifier: "Model Identifier",
  appleChip: "Apple Chip",
  systemFirmwareVersion: "System Firmware",
  osLoaderVersion: "OS Loader",
  hardwareUUID: "Hardware UUID",
  totalCores: "CPU Cores",
  coreDetails: "Core Details",
  wifiAddress: "Wi-Fi Address",
  bluetoothAddress: "Bluetooth Address",
  macAddress: "MAC Address",
  title: "Title",
});

// Fields to exclude from the dynamic display
export const excludeFields = ["confidence", "error", "aiEnhanced"];

// Get a display label for a field
export const getFieldLabel = (
  key: string,
  fieldLabels: Record<string, string>
): string => {
  return fieldLabels[key] || key;
};

/**
 * Format a field name to be JSON-compatible in camelCase
 * Examples:
 * - "Serial Number" -> "serialNumber"
 * - "RAM Size" -> "ramSize"
 * - "USB-C Ports" -> "usbcPorts"
 * - "Wi-Fi MAC" -> "wifiMac"
 */
export const formatFieldKey = (fieldName: string): string => {
  if (!fieldName) return "";

  // Remove any special characters except spaces and hyphens
  let formatted = fieldName.replace(/[^a-zA-Z0-9\s-]/g, "");

  // Replace hyphens with spaces for consistent processing
  formatted = formatted.replace(/-/g, " ");

  // Split by spaces
  const words = formatted.trim().split(/\s+/);

  // Convert to camelCase
  return words
    .map((word, index) => {
      // Convert to lowercase
      word = word.toLowerCase();

      // Capitalize first letter of each word except the first one
      if (index > 0) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }

      return word;
    })
    .join("");
};

// Format OS information
export const formatOSInfo = (osName?: string, osVersion?: string): string => {
  if (osName && osVersion) {
    if (osName.includes(osVersion)) {
      return osName;
    }
    return `${osName} ${osVersion}`;
  }
  return osName || "";
};

// Format field values
export const formatFieldValue = (
  key: string,
  value: any,
  deviceInfo: DeviceInfo
): string => {
  if (value === undefined || value === null || value === "") {
    return key === "title"
      ? "Required: Click to add a title for this record"
      : "Enter value";
  }

  if (typeof value === "string") {
    if (key === "osType" && deviceInfo.osVersion) {
      return `${value} | ${deviceInfo.osVersion}`;
    }

    if (key === "availableStorage" && deviceInfo.totalStorage) {
      return `${value} of ${deviceInfo.totalStorage}`;
    }

    if (key === "batteryLevel" && value) {
      return `${value}%`;
    }

    return value;
  }

  return String(value);
};
